import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Loader2, RefreshCw } from "lucide-react"
import { adminApi, type IntegrationQrCodeResponse } from "@/services/adminApi"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { IntegrationQrCodeResult } from "./IntegrationQrCodeResult"
import { getGenericUserErrorMessage } from "@/utils/userFacingErrors"

export const IntegrationQrGenerator = ({ merchantId }: { merchantId: string }) => {
  const { t } = useTranslation()
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState("")
  const [result, setResult] = useState<IntegrationQrCodeResponse | null>(null)

  const generate = async (signal?: AbortSignal) => {
    setIsGenerating(true)
    setError("")
    try {
      const response = await adminApi.generateIntegrationQrCode({ merchantId })
      if (signal?.aborted) return
      setResult(response)
    } catch {
      if (signal?.aborted) return
      setError(getGenericUserErrorMessage(t))
    } finally {
      if (!signal?.aborted) setIsGenerating(false)
    }
  }

  useEffect(() => {
    if (!merchantId) return
    const controller = new AbortController()
    void generate(controller.signal)
    return () => controller.abort()
  }, [merchantId])

  return (
    <Card data-testid="integration-qr-generator">
      <CardContent className="flex flex-col gap-4 p-4">
        <p className="text-sm text-foreground">{t("integrationInfo.qrCode.description")}</p>

        <div className="min-h-[240px]" data-testid="integration-qr-generator-state">
          {isGenerating ? (
            <div
              className="flex h-[240px] items-center justify-center gap-2 text-sm text-muted-foreground"
              data-testid="integration-qr-loading"
            >
              <Loader2 data-icon="inline-start" className="animate-spin" />
              {t("glasses.qrCode.generating")}
            </div>
          ) : error ? (
            <div className="flex flex-col gap-3">
              <Alert variant="destructive" data-testid="integration-qr-error">
                <AlertTitle>{t("integrationInfo.qrCode.errorTitle")}</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <Button
                className="self-start bg-foreground text-background shadow hover:bg-foreground/90"
                onClick={() => void generate()}
                disabled={isGenerating}
                data-testid="integration-qr-regenerate"
              >
                <RefreshCw data-icon="inline-start" />
                {t("integrationInfo.qrCode.regenerate")}
              </Button>
            </div>
          ) : result ? (
            <IntegrationQrCodeResult result={result} />
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
